/**
 * AuiFiles
 *
 * Surface-rendered virtual file system browser. The agent places
 * `{ type: 'Files', props: { ... } }` in a surface; the frontend
 * resolves the VFS owner (from props.ownerId or the surrounding canvas
 * context), lists the files via REST, subscribes to `vfs.file.*`
 * events for live updates, and renders a tree or list view with
 * preview, download, drag-and-drop upload, and (optional) delete.
 *
 * Host-agnostic by design: `ownerId` may be a conversation, KB, or
 * skill ID.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Calendar as CalendarIcon,
  Check,
  ChevronRight,
  Download,
  Folder,
  FolderOpen,
  FolderPlus,
  Loader2,
  MoreVertical,
  Pencil,
  RefreshCw,
  Search as SearchIcon,
  Trash2,
  Upload,
  X,
  List as ListIcon,
  FolderTree,
  Eye,
  Link2,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { iconForFile } from '@/utils/file-icon';
import { CanvasContext } from '@/contexts/CanvasContext';
import { useToast } from '@/components/ui/Toast';
import { DropZone } from '@/components/ui/DropZone';
import { ConfirmModal, Modal } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { formatFileSize, formatAppleDate } from '@/utils/format';
import { readDroppedEntries, MAX_DROPPED_FILES, type DroppedEntry } from '@/lib/dropped-entries';
import { downloadFileFromUrl } from '@/utils/download-file';
import { resolveCollisionFreePath } from '@/lib/vfs-collision';
import { useSurfaceNodeUiState } from '../AuiSurfaceContext';
import { AuiFilePreview } from './AuiFilePreview';
import { InlineEdit } from '@/components/ui/InlineEdit';
import {
  resolveFilesBackend,
  isKbOwner,
  type FilesBackend,
  type FilesEntry,
  type FilesEvent,
  type UseSearchResult,
} from './files-backend';
import {
  buildTree,
  buildLazyTree,
  sortChildren,
  flattenTree,
  reduceFolderStore,
  isNavigableRow,
  parentDir,
  type TreeNode,
  type FlatRow,
  type FolderStore,
  type FolderState,
} from './files-tree';
import type { AuiRendererProps } from '../registry';
import type { AuiNode } from '../../../types/canvases';
import type { VfsEntry } from '@/types/vfs';
import { vfsApi } from '@/services/api';
import { pruneRealisedFolders } from './files-backend/virtual-folders';

/** MIME used to mark intra-tree file drags. The payload is JSON
 *  `{ fileId, ownerId, path }`; folder + root drop handlers check for this
 *  data type before falling through to the external-files upload path. */
const VFS_DRAG_MIME = 'application/x-optr-vfs-file';

/** A pending cross-store copy (conversation VFS ↔ KB) awaiting either a
 *  collision decision or immediate execution. `convOwner` is always the
 *  conversation id the VFS copy endpoints key off; `destDir`/`destPath`
 *  address the target (this) store. */
type CrossCopyRequest =
  | {
      kind: 'to-kb';
      convOwner: string;
      kbId: string;
      srcConvPath: string;
      destDir: string;
      destPath: string;
    }
  | {
      kind: 'from-kb';
      convOwner: string;
      kbId: string;
      srcKbPath: string;
      destDir: string;
      destPath: string;
    };

/** Map a cross-store copy failure to a user-facing message. The VFS copy
 *  endpoints return real HTTP status codes carried on `ApiError.status`, so
 *  we switch on status — no string-matching. */
function crossCopyErrorMessage(err: unknown, name: string): string {
  const status = (err as { status?: number } | null)?.status;
  switch (status) {
    case 403:
      return "You don't have access to that knowledge base.";
    case 404:
      return `Couldn't copy ${name}: file or knowledge base not found.`;
    case 409:
      return `Couldn't copy ${name}: a folder is in the way at the destination.`;
    case 413:
      return `${name} is too large to copy (5 GB max).`;
    case 400:
      return `Couldn't copy ${name}: invalid request.`;
    default:
      return `Failed to copy ${name}.`;
  }
}

type ViewMode = 'tree' | 'list';

interface FilesProps {
  ownerId?: string;
  rootPath?: string;
  title?: string;
  allowUpload?: boolean;
  allowDelete?: boolean;
  showHidden?: boolean;
  initialView?: ViewMode;
  previewPath?: string;
  previewKey?: string;
}

/**
 * Persisted slice for a Files surface node.
 *
 * `view`             — last view mode the user picked (overrides `initialView`).
 * `folders`          — explicit user toggles. Absent paths fall back to
 *                      the default-open rule (open at depth < 2).
 * `lastSeenPreviewKey` — agent freshness token consumed by this component.
 *                      Prevents re-opening the preview after the user
 *                      dismissed it; agent re-asserts by sending a new key.
 */
/** Which timestamp to display in the date column. */
type DateColumn = 'created' | 'modified';

interface PersistedFilesSlice {
  view: ViewMode | null;
  folders: Record<string, boolean>;
  lastSeenPreviewKey: string | null;
  treeWidth: number | null;
  /** Path of the file the user had open in the preview pane. Restored on
   *  reload once `entries` resolves the path to a fresh VfsEntry. */
  previewPath: string | null;
  /** Which timestamp the date column renders. Defaults to 'modified'
   *  to match Finder. */
  dateColumn: DateColumn | null;
}

const PERSISTED_FILES_DEFAULT: PersistedFilesSlice = {
  view: null,
  folders: {},
  lastSeenPreviewKey: null,
  treeWidth: null,
  previewPath: null,
  dateColumn: null,
};

/** Default-open rule for folders the user hasn't touched.
 *  `1` = only top-level folders are open by default (matches the pre-
 *  persistence behavior). Deeper folders start collapsed. */
const DEFAULT_OPEN_DEPTH = 1;

/** Above this many visible rows, the tree windows its render with
 *  `@tanstack/react-virtual`; at or below it renders every row directly. */
const VIRTUALIZE_THRESHOLD = 100;

// Split-pane layout constants. Below STACK_BELOW_PX the panes stack
// vertically (tree above, preview below) and the resize handle hides.
const STACK_BELOW_PX = 600;
const TREE_MIN_PX = 200;
const TREE_MAX_PX = 500;
const TREE_DEFAULT_PX = 280;

/**
 * Shared state for folder-aware drag-and-drop. `TreeRow` reads this to
 * highlight itself when it's the active drop target, and routes drops
 * back to the panel via `processDrop`. `null` when no surface around
 * the row provides folder targets (defensive — drops still work via the
 * panel-level handler).
 */
type FilesDropContextValue = {
  activeDropPath: string | null;
  setActiveDropPath: (path: string | null) => void;
  /** Source folder of the currently-dragging file row (intra-tree drag).
   *  `null` when no drag is in progress or an external file drag is in
   *  flight. Folder rows and the panel header read this so they don't
   *  highlight themselves as drop targets when the drop would be a no-op
   *  (file dragged back into its own folder). */
  draggingSourceDir: string | null;
  setDraggingSourceDir: (path: string | null) => void;
  /** Funnel for drop events on folder rows, panel chrome, and file
   *  rows (intra-tree moves). Handles `VFS_DRAG_MIME` payloads as
   *  moves and external drops with folder-aware traversal + the
   *  20-file cap. Callers handle `preventDefault` / `stopPropagation`
   *  themselves before calling. `targetPath` is the destination folder
   *  (empty string for root). */
  processDrop: (e: React.DragEvent, targetPath: string) => Promise<void>;
};
const FilesDropContext = createContext<FilesDropContextValue | null>(null);

/**
 * Standalone-friendly props. AUI surface registration passes `{ node }`
 * (the AUI canvas contract); KB pages and other host surfaces can pass
 * `{ ownerId, title? }` to mount AuiFiles without a surface node. When
 * both are supplied, explicit `ownerId` wins so a host can override the
 * surface props if needed.
 */
export interface AuiFilesProps {
  node?: AuiNode;
  ownerId?: string;
  title?: string;
  /** Standalone-mount overrides for the inner surface props. When set they
   *  win over the surface node's own `allowUpload`/`allowDelete` — a host
   *  page (e.g. read-only plugin-managed skills) can lock the browser down. */
  allowUpload?: boolean;
  allowDelete?: boolean;
  /** Deep-link intent: open this file path in the preview pane. `previewKey`
   *  is a freshness token — a new value (re-)asserts the open even after the
   *  user dismissed it. Host KB pages set these from the `/f/:fileId` URL. */
  previewPath?: string;
  previewKey?: string;
  /** Fired when the previewed file changes (open, swap, or close → `null`).
   *  Host pages use it to push the selection into the URL. */
  onPreviewChange?: (entry: VfsEntry | null) => void;
  /** When provided, renders a "Copy link" action for files. Given the file's
   *  entry, returns the app-relative deep-link path (origin is prepended by
   *  the menu). Absent → no Copy-link affordance. */
  buildShareUrl?: (entry: VfsEntry) => string;
}

/**
 * Backend-resolving shell. Picks the right `FilesBackend` from the
 * resolved `ownerId` and keys the inner component on `kind:ownerId` so
 * the inner remounts cleanly when the backend swaps (legacy → KB or
 * vice-versa). React's hook-order invariant survives the swap that way.
 */
export function AuiFiles(props: AuiFilesProps | AuiRendererProps) {
  const node = (props as AuiFilesProps).node ?? (props as AuiRendererProps).node;
  const nodeProps = (node?.component.props ?? {}) as FilesProps;
  // Standalone-mount overrides (allowUpload/allowDelete) win over the surface
  // node's own values so a host can lock the browser read-only.
  const standalone = props as AuiFilesProps;
  const surfaceProps: FilesProps = {
    ...nodeProps,
    ...(standalone.allowUpload !== undefined ? { allowUpload: standalone.allowUpload } : {}),
    ...(standalone.allowDelete !== undefined ? { allowDelete: standalone.allowDelete } : {}),
    ...(standalone.previewPath !== undefined ? { previewPath: standalone.previewPath } : {}),
    ...(standalone.previewKey !== undefined ? { previewKey: standalone.previewKey } : {}),
  };
  const canvasCtx = useContext(CanvasContext);
  const ownerId =
    (props as AuiFilesProps).ownerId ||
    surfaceProps.ownerId ||
    canvasCtx?.conversationId ||
    undefined;

  const backend = useMemo(
    () => (ownerId ? resolveFilesBackend(ownerId) : null),
    [ownerId],
  );

  if (!ownerId || !backend) {
    return (
      <div className="rounded-md border border-dashed border-border p-6 text-sm text-muted-foreground">
        No file owner — pass `ownerId` or render inside a CanvasProvider.
      </div>
    );
  }

  return (
    <AuiFilesInner
      key={`${backend.kind}:${ownerId}`}
      backend={backend}
      node={node}
      surfaceProps={surfaceProps}
      ownerId={ownerId}
      titleOverride={(props as AuiFilesProps).title}
      onPreviewChange={standalone.onPreviewChange}
      buildShareUrl={standalone.buildShareUrl}
    />
  );
}

/** Flatten every loaded file child across the lazy store into a flat entry
 *  list, so the entries-coupled subsystems (preview restore, highlight, search
 *  open-result) keep working against "what's currently loaded". */
function collectLoadedFiles(store: FolderStore): FilesEntry[] {
  const out: FilesEntry[] = [];
  for (const state of store.values()) {
    for (const child of state.children) {
      if (child.kind === 'file' && child.entry) out.push(child.entry);
    }
  }
  return out;
}

/** Every ancestor folder path of a file path, root-first:
 *  `a/b/c/f.md` → `['a', 'a/b', 'a/b/c']`. Used to expand the tree down to a
 *  file so a revealed search result is visible in its folder. */
function ancestorFolderPaths(path: string): string[] {
  const parts = path.split('/');
  const out: string[] = [];
  for (let i = 1; i < parts.length; i++) out.push(parts.slice(0, i).join('/'));
  return out;
}

/**
 * Lazy per-folder loading store for KB-style backends. Loads a folder's
 * children on demand (paginated), reconciles live WS events in place, and
 * exposes the store + loaders. Inert when `enabled` is false (VFS keeps its
 * fetch-all `useEntries` path).
 */
function useLazyFolderStore(
  backend: FilesBackend,
  rootPath: string,
  enabled: boolean,
) {
  const [store, setStore] = useState<FolderStore>(() => new Map());
  const storeRef = useRef(store);
  storeRef.current = store;
  // Paths with an in-flight request — guards against double-loading the same
  // folder when the trigger effect re-runs before the first load resolves.
  const inflight = useRef<Set<string>>(new Set());
  const listChildren = backend.listChildren;

  const load = useCallback(
    async (path: string, mode: 'initial' | 'more') => {
      if (!enabled || !listChildren) return;
      const key = `${mode}:${path}`;
      if (inflight.current.has(key)) return;
      inflight.current.add(key);
      setStore((prev) => {
        const next = new Map(prev);
        const cur = next.get(path);
        next.set(path, {
          status: mode === 'more' ? 'loadingMore' : 'loading',
          children: mode === 'more' ? cur?.children ?? [] : cur?.children ?? [],
          cursor: cur?.cursor ?? null,
          hasMore: cur?.hasMore ?? false,
          totalCount: cur?.totalCount,
        });
        return next;
      });
      try {
        const cursor =
          mode === 'more' ? storeRef.current.get(path)?.cursor ?? undefined : undefined;
        const res = await listChildren(path, { cursor });
        setStore((prev) => {
          const next = new Map(prev);
          const cur = next.get(path);
          const base = mode === 'more' ? cur?.children ?? [] : [];
          next.set(path, {
            status: 'loaded',
            children: [...base, ...res.children],
            cursor: res.nextCursor,
            hasMore: res.hasMore,
            totalCount: res.totalCount,
          });
          return next;
        });
      } catch (err) {
        setStore((prev) => {
          const next = new Map(prev);
          const cur = next.get(path);
          next.set(path, {
            status: 'error',
            children: cur?.children ?? [],
            cursor: cur?.cursor ?? null,
            hasMore: cur?.hasMore ?? false,
            error: err instanceof Error ? err.message : String(err),
          });
          return next;
        });
      } finally {
        inflight.current.delete(key);
      }
    },
    [enabled, listChildren],
  );

  // Load a folder's first page if it has never been requested. Errors are NOT
  // auto-retried (would loop) — the error row's Retry button calls `reload`.
  const ensureLoaded = useCallback(
    (path: string) => {
      const status = storeRef.current.get(path)?.status;
      if (status === undefined || status === 'idle') void load(path, 'initial');
    },
    [load],
  );

  const loadMore = useCallback((path: string) => void load(path, 'more'), [load]);

  // Force-refresh one folder (Retry, or after a move into/out of it).
  const reload = useCallback((path: string) => void load(path, 'initial'), [load]);

  // Clear everything and re-load root + whichever folders are currently open.
  const reset = useCallback(
    (openPaths: string[]) => {
      inflight.current.clear();
      setStore(new Map());
      void load(rootPath, 'initial');
      for (const p of openPaths) void load(p, 'initial');
    },
    [load, rootPath],
  );

  // Optimistic folder insert (mkdir) — show the new folder immediately as an
  // empty, loaded folder; the `kb.vfs.folder.created` event later confirms.
  const optimisticFolder = useCallback((path: string) => {
    setStore((prev) => {
      const next = new Map(prev);
      const dir = parentDir(path);
      const parent = next.get(dir);
      if (parent && !parent.children.some((c) => c.path === path)) {
        next.set(dir, {
          ...parent,
          children: [
            ...parent.children,
            { kind: 'folder', path, name: path.split('/').pop() || path },
          ],
        });
      }
      if (!next.has(path)) {
        next.set(path, { status: 'loaded', children: [], cursor: null, hasMore: false });
      }
      return next;
    });
  }, []);

  const applyEvent = useCallback(
    (event: FilesEvent) => {
      if (!enabled) return;
      setStore((prev) => reduceFolderStore(prev, event).store);
      // A file landing in a folder realises any virtual placeholder for it.
      if (event.type === 'file-created' || event.type === 'file-updated') {
        pruneRealisedFolders(backend.ownerId, event.entry.path);
      } else if (event.type === 'file-moved') {
        pruneRealisedFolders(backend.ownerId, event.entry.path);
      }
    },
    [enabled, backend.ownerId],
  );

  const folderState = useCallback(
    (path: string): FolderState | undefined => store.get(path),
    [store],
  );

  return {
    store,
    rootStatus: store.get(rootPath)?.status,
    ensureLoaded,
    loadMore,
    reload,
    reset,
    optimisticFolder,
    applyEvent,
    folderState,
  };
}

function AuiFilesInner({
  backend,
  node,
  surfaceProps,
  ownerId,
  titleOverride,
  onPreviewChange,
  buildShareUrl,
}: {
  backend: FilesBackend;
  node: AuiNode | undefined;
  surfaceProps: FilesProps;
  ownerId: string;
  titleOverride?: string;
  onPreviewChange?: (entry: VfsEntry | null) => void;
  buildShareUrl?: (entry: VfsEntry) => string;
}) {
  const props = surfaceProps;

  const title = titleOverride ?? props.title ?? ownerTypeLabel(ownerId);
  const allowUpload = props.allowUpload ?? true;
  const allowDelete = props.allowDelete ?? true;
  const showHidden = props.showHidden ?? false;
  const rootPath = (props.rootPath ?? '').replace(/^\/+|\/+$/g, '');

  // Persisted slice — view mode, folder toggles, last-seen preview key.
  // Falls back to component-local state when there's no CanvasProvider
  // (e.g. on the dev fixture page, or any standalone host like the KB
  // pages — which is what the `standalone:` synthetic id signals).
  const persistenceNodeId = node?.id ?? `standalone:${ownerId}`;
  const [persisted, setPersisted] = useSurfaceNodeUiState<'files', PersistedFilesSlice>(
    persistenceNodeId,
    'files',
    PERSISTED_FILES_DEFAULT,
  );

  // Local mirrors — written through to `persisted` via the effect below.
  const [view, setView] = useState<ViewMode>(
    persisted.view ?? props.initialView ?? 'tree',
  );
  const [folders, setFolders] = useState<Record<string, boolean>>(persisted.folders);
  const [lastSeenPreviewKey, setLastSeenPreviewKey] = useState<string | null>(
    persisted.lastSeenPreviewKey,
  );
  const [treeWidth, setTreeWidth] = useState<number>(
    clamp(persisted.treeWidth ?? TREE_DEFAULT_PX, TREE_MIN_PX, TREE_MAX_PX),
  );
  // Persisted "user intent" — survives unmount. Separate from `previewEntry`
  // (the live VfsEntry reference) because the entry may not be in `entries`
  // yet at mount time, but we still want to remember which path to restore.
  const [intendedPreviewPath, setIntendedPreviewPath] = useState<string | null>(
    persisted.previewPath,
  );
  const [dateColumn, setDateColumn] = useState<DateColumn>(
    persisted.dateColumn ?? 'modified',
  );

  // Persist on local-state divergence (mirrors AuiTable's pattern).
  const lastSentRef = useRef<string | null>(null);
  useEffect(() => {
    const next: PersistedFilesSlice = {
      view,
      folders,
      lastSeenPreviewKey,
      treeWidth,
      previewPath: intendedPreviewPath,
      dateColumn,
    };
    const serialized = JSON.stringify(next);
    if (lastSentRef.current === null) {
      lastSentRef.current = JSON.stringify(persisted);
    }
    if (serialized === lastSentRef.current) return;
    lastSentRef.current = serialized;
    setPersisted(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, folders, lastSeenPreviewKey, treeWidth, intendedPreviewPath, dateColumn]);

  // Container width drives the stacked/side-by-side layout switch. Observed
  // on the outer card so embedding surfaces of any width adapt correctly
  // (a narrow chat side-panel and a wide canvas behave appropriately).
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(Number.POSITIVE_INFINITY);
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number') setContainerWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const isStacked = containerWidth < STACK_BELOW_PX;

  const [previewEntry, setPreviewEntry] = useState<VfsEntry | null>(null);
  // A search result the user clicked whose file isn't in the loaded tree yet.
  // We reveal + lazy-load its folder, then a resolution effect opens the
  // preview once the entry lands in `entries` (mirrors the `previewKey` effect).
  const [pendingOpenPath, setPendingOpenPath] = useState<string | null>(null);
  const [deleteEntry, setDeleteEntry] = useState<VfsEntry | null>(null);
  const [deleteFolderPath, setDeleteFolderPath] = useState<string | null>(null);
  // Cross-store drag-and-drop copy awaiting a collision decision.
  const [pendingCopy, setPendingCopy] = useState<CrossCopyRequest | null>(null);
  // fileId of the row currently in inline-rename mode. One row at a time;
  // a different row entering rename mode replaces the previous selection.
  const [renamingFileId, setRenamingFileId] = useState<string | null>(null);

  // Lazy backends (KB) load folder children on demand; eager backends (VFS)
  // ship the whole list through `useEntries`. We merge both into a single
  // `entries` value so every entries-coupled subsystem below works unchanged.
  const isLazy = backend.capabilities.lazyChildren;
  const lazy = useLazyFolderStore(backend, rootPath, isLazy);

  const {
    entries: backendEntries,
    isLoading: backendLoading,
    error,
    refetch: backendRefetch,
  } = backend.useEntries();
  const { jobs, uploadFile } = backend.useUpload();
  const { deleteFile, deleteFolder, isDeleting } = backend.useDelete();
  const { folders: extraFolderPaths, mkdir, isCreating: isCreatingFolder } =
    backend.useFolders();

  // Loaded file entries for the lazy store (used by the entries-coupled
  // subsystems: preview restore, highlight, search-open, collision dedupe).
  const lazyEntries = useMemo(
    () => (isLazy ? collectLoadedFiles(lazy.store) : []),
    [isLazy, lazy.store],
  );
  const entries: FilesEntry[] = isLazy ? lazyEntries : (backendEntries as FilesEntry[]);
  const isLoading = isLazy ? lazy.rootStatus === 'loading' : backendLoading;

  // Per-owner semantic search. The hook is only called when the
  // backend declares the capability; otherwise we skip rendering the
  // affordance entirely. Conditional hook call is safe because
  // `backend.capabilities.search` is stable per backend instance
  // (the parent remounts via `key={backend.kind}:{ownerId}` on swap).
  const searchOp = backend.capabilities.search && backend.useSearch
    ? backend.useSearch()
    : null;
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const toast = useToast();

  // Live ref over the VFS entries list so async upload handlers can
  // build a collision-resolved name against the latest state without
  // re-running the surrounding useCallback on every entries change.
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  // Unified refresh: eager backends refetch the whole list; lazy backends
  // clear the store and re-load root + whichever folders are currently open.
  const foldersRef = useRef(folders);
  foldersRef.current = folders;
  const refresh = useCallback(async () => {
    if (isLazy) {
      const openPaths = Object.entries(foldersRef.current)
        .filter(([, v]) => v)
        .map(([k]) => k);
      lazy.reset(openPaths);
    } else {
      await backendRefetch();
    }
  }, [isLazy, lazy, backendRefetch]);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await refresh();
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, refresh]);

  // Notify the host of preview changes without destabilising `openPreview`
  // (the callback prop may get a fresh identity each render; a ref keeps the
  // memoised `openPreview` — and the effects that depend on it — stable).
  const onPreviewChangeRef = useRef(onPreviewChange);
  onPreviewChangeRef.current = onPreviewChange;

  // User-initiated open/close of the preview pane. Updates BOTH the live
  // entry reference and the persisted "intent" path so the choice survives
  // a reload, and notifies the host (URL deep-link sync). The vfs-sync effect
  // below uses raw `setPreviewEntry` because it's just refreshing the entry —
  // the user's intent (path) is unchanged.
  const openPreview = useCallback((entry: VfsEntry | null) => {
    setPreviewEntry(entry);
    setIntendedPreviewPath(entry?.path ?? null);
    onPreviewChangeRef.current?.(entry);
  }, []);

  // Reveal a file by path and open its preview. If it's already loaded, open
  // immediately; otherwise expand every ancestor folder, lazy-load them, and
  // hand off to the `pendingOpenPath` resolution effect which opens the preview
  // once the entry lands (paging / statting as needed). Shared by search-result
  // open and deep-link (URL) restore.
  //
  // We prefetch EVERY ancestor folder up front rather than just the immediate
  // parent. The lazy store loads a folder by its full path independently, so
  // firing them together lets the whole chain load concurrently (one round-trip
  // of latency). Leaving the intermediate folders to load as the tree renders
  // them instead cascades into one sequential round-trip per level — e.g.
  // revealing `a/b/c/f.md` would load root→a→a/b→a/b/c in series.
  const revealAndOpen = useCallback(
    (path: string) => {
      const entry = entries.find((e) => e.path === path);
      if (entry) {
        openPreview(entry);
        return;
      }
      const ancestors = ancestorFolderPaths(path);
      setFolders((prev) => {
        const next = { ...prev };
        for (const a of ancestors) next[a] = true;
        return next;
      });
      // ensureLoaded is deduped (in-flight + status guarded), so the tree's own
      // render-driven loads for these same folders simply no-op afterwards.
      for (const a of ancestors) lazy.ensureLoaded(a);
      setPendingOpenPath(path);
    },
    [entries, lazy, openPreview],
  );

  // Restore preview on mount once entries arrive: resolve the persisted
  // path to a live entry. Re-runs when entries change so the restore also
  // catches files that arrive after mount (vfs.file.created race).
  useEffect(() => {
    if (previewEntry) return;
    if (!intendedPreviewPath) return;
    const match = entries.find((e) => e.path === intendedPreviewPath);
    if (match) setPreviewEntry(match);
  }, [entries, previewEntry, intendedPreviewPath]);

  // Preview intent from a new `previewKey` — set by an agent directing the
  // user to a file, or by a host page resolving a `/f/:fileId` deep link.
  useEffect(() => {
    const { previewKey, previewPath } = props;
    if (!previewKey || !previewPath) return;
    if (previewKey === lastSeenPreviewKey) return;
    const match = entries.find((e) => e.path === previewPath);
    if (match) {
      openPreview(match);
      setLastSeenPreviewKey(previewKey);
      return;
    }
    // Not in the loaded tree. On lazy backends (KB) the file's folder may
    // simply be collapsed (a reload / shared deep link) — reveal + lazy-load
    // it and let the `pendingOpenPath` resolution effect open it once it
    // lands. On eager backends `entries` is the full list streaming in, so a
    // miss just means "not arrived yet": wait, and this effect re-runs as
    // entries update (revealing via pendingOpenPath would wrongly hard-error).
    if (isLazy) {
      setLastSeenPreviewKey(previewKey);
      revealAndOpen(previewPath);
    }
  }, [props.previewKey, props.previewPath, entries, lastSeenPreviewKey, isLazy, openPreview, revealAndOpen, props]);

  // Resolve a search result the user clicked whose file isn't in the loaded
  // tree yet (`pendingOpenPath`, set by `onOpenResult`). `onOpenResult` reveals
  // + lazy-loads the file's folder; this effect opens the preview once the
  // entry surfaces in `entries`. If the folder is paginated it pages through;
  // if the file isn't a direct child (stale/renamed search hit) it stat-confirms
  // once and injects it, else surfaces the error toast.
  const statFallbackRef = useRef<string | null>(null);
  const { folderState: lazyResolveFolderState, loadMore: lazyResolveLoadMore, applyEvent: lazyResolveApplyEvent } = lazy;
  useEffect(() => {
    const path = pendingOpenPath;
    if (!path) return;
    const match = entries.find((e) => e.path === path);
    if (match) {
      openPreview(match);
      setPendingOpenPath(null);
      statFallbackRef.current = null;
      return;
    }
    if (!isLazy) {
      // Eager backend: `entries` is the full list — a miss is terminal.
      toast.error('File not in current tree — refresh and retry.');
      setPendingOpenPath(null);
      return;
    }
    const dir = parentDir(path);
    const state = lazyResolveFolderState(dir);
    const status = state?.status;
    if (status === undefined || status === 'loading' || status === 'loadingMore') {
      return; // folder still loading — the effect re-runs when the store updates
    }
    if (status === 'error') {
      toast.error('Couldn’t load the file’s folder — refresh and retry.');
      setPendingOpenPath(null);
      return;
    }
    if (state?.hasMore) {
      lazyResolveLoadMore(dir); // file may be on a later page — keep paging
      return;
    }
    // Folder fully loaded and the file isn't a direct child. Stat-confirm the
    // path once; if it exists, inject it so `entries` picks it up next render,
    // otherwise the search hit is stale (moved/deleted).
    if (statFallbackRef.current === path) return;
    statFallbackRef.current = path;
    void (async () => {
      const entry = backend.statEntry ? await backend.statEntry(path) : null;
      if (entry) {
        lazyResolveApplyEvent({ type: 'file-created', entry });
      } else {
        toast.error('Couldn’t open that file — it may have been moved or deleted.');
        setPendingOpenPath(null);
        statFallbackRef.current = null;
      }
    })();
  }, [
    entries,
    pendingOpenPath,
    isLazy,
    openPreview,
    lazyResolveFolderState,
    lazyResolveLoadMore,
    lazyResolveApplyEvent,
    backend,
    toast,
  ]);

  // Keep the previewed entry in sync with the live entries list. When the
  // file is edited (`vfs.file.updated`), entries updates with a fresh
  // `updatedAt` and the preview's renderers cache-bust on that field — but
  // only if `previewEntry` itself is also the fresh reference. If the file
  // was deleted out from under us, close the preview.
  useEffect(() => {
    if (!previewEntry) return;
    const fresh = entries.find((e) => e.fileId === previewEntry.fileId);
    if (!fresh) {
      openPreview(null);
      return;
    }
    if (fresh === previewEntry) return;
    if (
      fresh.updatedAt === previewEntry.updatedAt &&
      fresh.size === previewEntry.size &&
      fresh.path === previewEntry.path &&
      fresh.name === previewEntry.name &&
      fresh.contentType === previewEntry.contentType
    ) return;
    setPreviewEntry(fresh);
    // When a rename/move changes the preview file's path, also bump the
    // persisted intent so reload still restores the right file. Only
    // re-anchor when the intent was tracking the old path — if the user
    // had already explicitly cleared it, don't write it back.
    if (fresh.path !== previewEntry.path && intendedPreviewPath === previewEntry.path) {
      setIntendedPreviewPath(fresh.path);
    }
  }, [entries, previewEntry, openPreview, intendedPreviewPath]);

  // Auto-expand ancestor folders whenever a file path appears that wasn't
  // present on the previous render. Covers `vfs.file.created` (brand-new
  // path) and `vfs.file.moved` (existing fileId at a new path). Gated on
  // `isLoading` so the initial cold-load (where every entry looks "new"
  // relative to an empty set) doesn't re-expand the whole tree — we seed
  // the ref on the first post-load run and only later deltas trigger it.
  const seenPathsRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    // Lazy backends are collapsed by default and reconcile arrivals into the
    // per-folder store; auto-expanding ancestors on every loaded-file delta
    // would yank the viewport, so skip it (local mkdir/upload opens its own
    // target explicitly).
    if (isLazy) return;
    if (isLoading) return;
    const current = new Set(entries.map((e) => e.path));
    const prev = seenPathsRef.current;
    seenPathsRef.current = current;
    if (prev === null) return;

    const ancestors = new Set<string>();
    for (const path of current) {
      if (prev.has(path)) continue;
      const parts = path.split('/');
      for (let i = 1; i < parts.length; i++) {
        ancestors.add(parts.slice(0, i).join('/'));
      }
    }
    if (ancestors.size === 0) return;

    setFolders((prevFolders) => {
      let changed = false;
      const next = { ...prevFolders };
      for (const a of ancestors) {
        if (next[a] !== true) {
          next[a] = true;
          changed = true;
        }
      }
      return changed ? next : prevFolders;
    });
  }, [entries, isLoading, isLazy]);

  // Soft alert for freshly-changed entries. When a file is created,
  // updated, or moved (per WS event), its name text tints to the primary
  // color, then fades back to `text-foreground` over HIGHLIGHT_DURATION_MS.
  // Ancestor folders of any highlighted file get the same treatment so the
  // change is visible even when the file is deep in the tree.
  //
  // Implementation is a `Map<fileId, { since, seq }>` here + a derived
  // `Set<folderPath>` below. `seq` increments on each re-trigger so the
  // animated span can be `key={seq}`-remounted — CSS animations don't
  // restart when the same class is reapplied to the same DOM node.
  // Match the `file-highlight-fade` animation total duration in
  // tailwind.config.js (15s solid primary + 1.5s fade = 16.5s). The
  // cleanup timer drops the fileId from `highlighted` after the
  // animation completes so the span re-keys back to the plain name.
  const HIGHLIGHT_DURATION_MS = 16_500;
  const [highlighted, setHighlighted] = useState<Map<string, { since: number; seq: number }>>(
    () => new Map(),
  );
  const cleanupTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Flag fileIds as freshly-changed: bump each one's `seq` (so the animated
  // name span re-mounts and restarts the CSS fade) and schedule its cleanup.
  const triggerHighlight = useCallback((fileIds: string[]) => {
    if (fileIds.length === 0) return;
    setHighlighted((current) => {
      const updated = new Map(current);
      for (const fileId of fileIds) {
        const prevEntry = updated.get(fileId);
        updated.set(fileId, { since: Date.now(), seq: (prevEntry?.seq ?? 0) + 1 });
        const existingTimer = cleanupTimersRef.current.get(fileId);
        if (existingTimer) clearTimeout(existingTimer);
        const timer = setTimeout(() => {
          cleanupTimersRef.current.delete(fileId);
          setHighlighted((c) => {
            if (!c.has(fileId)) return c;
            const n = new Map(c);
            n.delete(fileId);
            return n;
          });
        }, HIGHLIGHT_DURATION_MS);
        cleanupTimersRef.current.set(fileId, timer);
      }
      return updated;
    });
  }, []);

  // Live WS events: reconcile the lazy store, and — for lazy backends — drive
  // the freshly-changed highlight from GENUINE events only. (A file that
  // appears because its folder was just lazily loaded is not new, so lazy
  // backends must NOT use the entries-diff effect below.) Subscription is
  // stable per backend instance (parent remounts on `key={kind}:{ownerId}`),
  // so this conditional hook call is safe — mirrors the `useSearch` pattern.
  backend.useOnEvent?.((event) => {
    lazy.applyEvent(event);
    if (
      isLazy &&
      (event.type === 'file-created' ||
        event.type === 'file-updated' ||
        event.type === 'file-moved')
    ) {
      triggerHighlight([event.entry.fileId]);
    }
  });

  // EAGER backends: detect created/updated/moved by diffing the in-memory
  // entries snapshot. `null` until the first post-load render so the cold-load
  // doesn't flash every row. Lazy backends skip this entirely (see above).
  const highlightSnapshotRef = useRef<Map<string, { path: string; updatedAt: string }> | null>(
    null,
  );
  useEffect(() => {
    if (isLazy) return;
    if (isLoading) return;
    const next = new Map<string, { path: string; updatedAt: string }>();
    for (const e of entries) next.set(e.fileId, { path: e.path, updatedAt: e.updatedAt });
    const prev = highlightSnapshotRef.current;
    highlightSnapshotRef.current = next;
    if (prev === null) return;

    const triggered: string[] = [];
    for (const [fileId, cur] of next) {
      const old = prev.get(fileId);
      if (!old) {
        triggered.push(fileId); // created
      } else if (old.path !== cur.path || old.updatedAt !== cur.updatedAt) {
        triggered.push(fileId); // updated or moved
      }
    }
    triggerHighlight(triggered);
  }, [entries, isLoading, isLazy, triggerHighlight]);
  // Clear any pending cleanup timers on unmount so they don't fire against
  // a torn-down component.
  useEffect(() => {
    const timers = cleanupTimersRef.current;
    return () => {
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
    };
  }, []);

  // Derive ancestor folder paths from currently-highlighted fileIds. For
  // typical tree sizes this linear pass is cheap; promote to a fileId→path
  // index if profiling ever flags it.
  const highlightedFolderPaths = useMemo(() => {
    const out = new Set<string>();
    if (highlighted.size === 0) return out;
    for (const fileId of highlighted.keys()) {
      const entry = entries.find((e) => e.fileId === fileId);
      if (!entry) continue;
      const parts = entry.path.split('/');
      for (let i = 1; i < parts.length; i++) {
        out.add(parts.slice(0, i).join('/'));
      }
    }
    return out;
  }, [entries, highlighted]);

  // Lazy backends start fully collapsed (default-open depth 0) so mounting a
  // large KB only loads the root — every other folder loads on explicit
  // expand. Eager backends keep the historical "top level open" behaviour.
  const defaultOpenDepth = isLazy ? 0 : DEFAULT_OPEN_DEPTH;

  const toggleFolder = useCallback((path: string) => {
    setFolders((prev) => {
      const next = { ...prev };
      // Compute current state (explicit toggle wins over default rule).
      const segments = path.split('/').filter(Boolean).length;
      const defaultOpen = segments <= defaultOpenDepth;
      const current = prev[path] ?? defaultOpen;
      next[path] = !current;
      return next;
    });
  }, [defaultOpenDepth]);

  const isFolderOpen = useCallback((path: string, depth: number) => {
    if (path in folders) return folders[path];
    return depth + 1 <= defaultOpenDepth;
  }, [folders, defaultOpenDepth]);

  // Filter to rootPath + hidden setting.
  const visible = useMemo(() => {
    return entries.filter((e) => {
      if (rootPath && !e.path.startsWith(`${rootPath}/`) && e.path !== rootPath) return false;
      if (!showHidden && e.path.split('/').some((seg) => seg.startsWith('.'))) return false;
      return true;
    });
  }, [entries, rootPath, showHidden]);

  // Materialise the tree and flatten it to virtualizable rows. Eager backends
  // build from the in-memory list (alphabetical); lazy backends build from the
  // per-folder store, preserving the backend's server order and emitting
  // spinner/empty/error/load-more sentinel rows. One renderer consumes
  // `flatRows` for both.
  const lazyFolderState = lazy.folderState;
  const topNodes = useMemo<TreeNode[]>(() => {
    if (isLazy) {
      const root = buildLazyTree(lazy.store, rootPath);
      return Array.from(root.children.values());
    }
    const root = buildTree(visible, rootPath, extraFolderPaths ?? []);
    return sortChildren(root);
  }, [isLazy, lazy.store, visible, rootPath, extraFolderPaths]);

  const flatRows = useMemo<FlatRow[]>(
    () =>
      flattenTree(topNodes, {
        isFolderOpen,
        lazy: isLazy,
        folderState: lazyFolderState,
      }),
    [topNodes, isFolderOpen, isLazy, lazyFolderState],
  );

  // Lazy load triggers: the root once, then any folder the user opens
  // (including persisted-open folders restored from a prior session).
  // `ensureLoaded` no-ops for already loading/loaded/errored folders.
  const lazyEnsureLoaded = lazy.ensureLoaded;
  useEffect(() => {
    if (isLazy) lazyEnsureLoaded(rootPath);
  }, [isLazy, rootPath, lazyEnsureLoaded]);
  useEffect(() => {
    if (!isLazy) return;
    for (const row of flatRows) {
      if (row.variant === 'folder' && row.open) lazyEnsureLoaded(row.path);
    }
  }, [isLazy, flatRows, lazyEnsureLoaded]);

  /**
   * Upload a batch of dropped/picked entries into the VFS. Handles
   * folder hierarchy (entries with a slash in their relativePath land
   * in nested folders under the base prefix) and renames collisions
   * with existing files using the OS-style `name (1).ext` convention,
   * so an upload never silently overwrites a sibling.
   *
   * In-batch dedupe: two `test.md` entries in the same drop don't both
   * pick `test.md` — the second becomes `test (1).md` because we
   * accumulate resolved paths into the reserved set as we go.
   */
  const handleUploads = useCallback(
    async (entries: DroppedEntry[], folderPrefix?: string) => {
      if (!ownerId) return;
      if (entries.length === 0) return;
      const base = ((folderPrefix ?? rootPath) ?? '').replace(/^\/+|\/+$/g, '');

      // Seed the reserved set with every existing file in the VFS so
      // any same-path drop triggers a rename. Resolved paths from this
      // batch get added below so in-batch duplicates also dedupe.
      const reserved = new Set<string>(entriesRef.current.map((e) => e.path));

      for (const { file, relativePath } of entries) {
        const lastSlash = relativePath.lastIndexOf('/');
        const subDir = lastSlash >= 0 ? relativePath.slice(0, lastSlash) : '';
        const basename = lastSlash >= 0 ? relativePath.slice(lastSlash + 1) : relativePath;
        const folderPath = subDir
          ? (base ? `${base}/${subDir}` : subDir)
          : base;
        const { name, path } = resolveCollisionFreePath(basename, folderPath, reserved);
        reserved.add(path);
        const result = await uploadFile(
          file,
          folderPath ? { prefix: folderPath, name } : { name },
        );
        if (!result) toast.error(`Failed to upload ${basename}`);
      }
    },
    [ownerId, uploadFile, rootPath, toast],
  );

  /**
   * Thin wrapper preserving the existing `File[]` callsites (file
   * picker, header upload button). Each picked file becomes a flat
   * entry (no folder hierarchy) and inherits the same collision +
   * upload pipeline as drag-and-drop.
   */
  const handleFiles = useCallback(
    (files: File[], folderPrefix?: string) =>
      handleUploads(
        files.map((f) => ({ file: f, relativePath: f.name })),
        folderPrefix,
      ),
    [handleUploads],
  );

  // Single funnel for both inline-rename and drag-and-drop moves. The
  // legacy VFS backend emits `vfs.file.moved` events that drive the
  // list view update in-place. KB owners don't emit a fast-enough
  // event yet (and not all backends necessarily do), so we also force
  // a refetch immediately after the mutation. The two converge to the
  // same state, so the extra refetch is benign even on VFS.
  const doMove = useCallback(
    async (from: string, to: string) => {
      if (from === to) return;
      try {
        await backend.move({ from, to });
        if (isLazy) {
          // Reload both ends so the moved row leaves its old folder and
          // appears in the new one (the WS move event also reconciles, but
          // a targeted reload is immediate and order-correct).
          lazy.reload(parentDir(from));
          lazy.reload(parentDir(to));
        } else {
          await backendRefetch();
        }
      } catch (err) {
        const e = err as { status?: number; message?: string };
        if (e?.status === 409) {
          toast.error(`"${to.split('/').pop()}" already exists`);
        } else {
          toast.error(`Failed to move ${from.split('/').pop() ?? from}`);
        }
      }
    },
    [backend, isLazy, lazy, backendRefetch, toast],
  );

  // Drag-and-drop state lives on the panel, not the per-row primitive,
  // so the panel handles "anywhere not on a folder" → root uploads, and
  // each folder row reports its own hover via the shared context.
  const [isDragging, setIsDragging] = useState(false);
  const [activeDropPath, setActiveDropPath] = useState<string | null>(null);
  // Source folder of the file row currently being dragged. Drives the
  // "skip highlight on source" rule for folder rows + root header.
  const [draggingSourceDir, setDraggingSourceDir] = useState<string | null>(null);
  const dragDepthRef = useRef(0);

  const resetDrag = useCallback(() => {
    dragDepthRef.current = 0;
    setIsDragging(false);
    setActiveDropPath(null);
    setDraggingSourceDir(null);
  }, []);

  // Execute a cross-store copy (conversation VFS ↔ KB) via the server-side
  // copy endpoints — bytes move at the S3 level, no client upload/download.
  // No manual refetch: the target panel refreshes from the WS event the
  // endpoint emits (`kb.vfs.file.created` on import, `kb.vfs.file.created` /
  // `vfs.file.created` on export), which `useKbCacheReconciler` / `useVfsFiles`
  // both consume — `useVfsFiles` listens to the `kb.vfs.*` namespace too.
  const runCrossCopy = useCallback(
    async (req: CrossCopyRequest, destPath: string) => {
      const name = destPath.split('/').pop() ?? destPath;
      try {
        if (req.kind === 'to-kb') {
          await vfsApi.copyToKb(req.convOwner, {
            path: req.srcConvPath,
            kbId: req.kbId,
            kbPath: destPath,
            mode: 'copy',
          });
        } else {
          await vfsApi.copyFromKb(req.convOwner, {
            path: destPath,
            kbId: req.kbId,
            kbPath: req.srcKbPath,
            mode: 'copy',
          });
        }
        toast.success(`Copied ${name}`);
      } catch (err) {
        toast.error(crossCopyErrorMessage(err, name));
      }
    },
    [toast],
  );

  // A file dragged from the *other* panel. Resolve the transfer direction
  // from this (target) panel's owner vs the dragged file's owner, then copy
  // into this store — prompting first when the destination name is taken.
  const crossStoreCopy = useCallback(
    (ref: { ownerId: string; path: string }, targetDir: string) => {
      const filename = ref.path.split('/').pop() ?? ref.path;
      const destPath = targetDir ? `${targetDir}/${filename}` : filename;
      const srcKb = isKbOwner(ref.ownerId);
      const dstKb = isKbOwner(ownerId);
      let req: CrossCopyRequest;
      if (!srcKb && dstKb) {
        // import: conversation → KB (conversation owner = source).
        req = {
          kind: 'to-kb',
          convOwner: ref.ownerId,
          kbId: ownerId,
          srcConvPath: ref.path,
          destDir: targetDir,
          destPath,
        };
      } else if (srcKb && !dstKb) {
        // export: KB → conversation (conversation owner = target).
        req = {
          kind: 'from-kb',
          convOwner: ownerId,
          kbId: ref.ownerId,
          srcKbPath: ref.path,
          destDir: targetDir,
          destPath,
        };
      } else {
        // KB↔KB / conversation↔conversation aren't covered by the endpoints.
        toast.error("Copying between these two locations isn't supported yet.");
        return;
      }
      // Collision against this (target) panel's listing → prompt before the
      // endpoint overwrites in place.
      if (entriesRef.current.some((e) => e.path === destPath)) {
        setPendingCopy(req);
        return;
      }
      void runCrossCopy(req, destPath);
    },
    [ownerId, runCrossCopy, toast],
  );

  /**
   * Unified drop entry point. Branches on the VFS_DRAG_MIME payload to
   * route intra-tree moves through `doMove`, and runs folder-aware
   * traversal + 20-cap rejection for external drops before handing the
   * entries to `handleUploads`. Folder rows, the panel chrome, and the
   * file-row drag-out flow all funnel through here so the move/upload
   * semantics stay in one place.
   *
   * The caller owns `preventDefault` / `stopPropagation`; this helper
   * assumes the event has been claimed.
   */
  const processDrop = useCallback(
    async (e: React.DragEvent, targetPath: string) => {
      resetDrag();
      const raw = e.dataTransfer.getData(VFS_DRAG_MIME);
      if (raw) {
        try {
          const ref = JSON.parse(raw) as { fileId: string; ownerId: string; path: string };
          if (ref.ownerId !== ownerId) {
            // Cross-panel: copy between stores (conversation VFS ↔ KB).
            crossStoreCopy(ref, targetPath);
            return;
          }
          const lastSlash = ref.path.lastIndexOf('/');
          const sourceDir = lastSlash >= 0 ? ref.path.slice(0, lastSlash) : '';
          if (sourceDir === targetPath) return; // same folder: no-op
          const filename = ref.path.split('/').pop() ?? ref.path;
          const toPath = targetPath ? `${targetPath}/${filename}` : filename;
          void doMove(ref.path, toPath);
        } catch {
          // Malformed payload — silently ignore.
        }
        return;
      }
      // External file/folder drop: walk the entry tree before touching
      // VFS state so the 20-file cap can reject the whole drop atomically.
      const result = await readDroppedEntries(e.dataTransfer);
      if (result.kind === 'too-many') {
        toast.error(
          `Drop limited to ${MAX_DROPPED_FILES} files (you dropped ${result.count}). Try a smaller selection.`,
        );
        return;
      }
      if (result.entries.length === 0) return;
      void handleUploads(result.entries, targetPath);
    },
    [resetDrag, ownerId, doMove, crossStoreCopy, handleUploads, toast],
  );

  const dropCtxValue = useMemo<FilesDropContextValue>(
    () => ({
      activeDropPath,
      setActiveDropPath,
      draggingSourceDir,
      setDraggingSourceDir,
      processDrop,
    }),
    [activeDropPath, draggingSourceDir, processDrop],
  );

  const handlePanelDragEnter = useCallback((e: React.DragEvent) => {
    if (!allowUpload) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    if (dragDepthRef.current === 1) setIsDragging(true);
  }, [allowUpload]);

  const handlePanelDragOver = useCallback((e: React.DragEvent) => {
    if (!allowUpload) return;
    e.preventDefault();
  }, [allowUpload]);

  const handlePanelDragLeave = useCallback((e: React.DragEvent) => {
    if (!allowUpload) return;
    e.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDragging(false);
      setActiveDropPath(null);
    }
  }, [allowUpload]);

  const handlePanelDrop = useCallback((e: React.DragEvent) => {
    if (!allowUpload) return;
    e.preventDefault();
    // Drops anywhere on panel chrome (outside any folder row) target
    // the configured rootPath. processDrop handles both intra-tree
    // moves and external file/folder traversal + the 20-file cap.
    void processDrop(e, rootPath || '');
  }, [allowUpload, processDrop, rootPath]);

  // Resize-handle drag — mirrors OperatorChatPanel's pattern: register
  // listeners on `document` so the drag survives the cursor leaving the
  // handle, clamp on each move, detach on mouseup. Stacked layout hides
  // the handle entirely so this is never engaged in that mode.
  const dragStateRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      dragStateRef.current = { startX: e.clientX, startWidth: treeWidth };
      const onMove = (ev: MouseEvent) => {
        const drag = dragStateRef.current;
        if (!drag) return;
        const next = clamp(drag.startWidth + (ev.clientX - drag.startX), TREE_MIN_PX, TREE_MAX_PX);
        setTreeWidth(next);
      };
      const onUp = () => {
        dragStateRef.current = null;
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [treeWidth],
  );

  // Hidden file picker used by both the header Upload button (which still
  // wraps DropZone for drag-and-drop) and the folder-row "Upload to this
  // folder" menu items. We track which folder to target via a ref so the
  // picker can be a single DOM element shared across rows.
  const folderUploadInputRef = useRef<HTMLInputElement | null>(null);
  const pendingUploadFolderRef = useRef<string | null>(null);
  const triggerFolderUpload = useCallback((folderPath: string) => {
    pendingUploadFolderRef.current = folderPath;
    folderUploadInputRef.current?.click();
  }, []);
  const handleFolderUploadInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      const folder = pendingUploadFolderRef.current;
      pendingUploadFolderRef.current = null;
      // Reset so picking the same file twice in a row still fires `change`.
      e.target.value = '';
      if (files.length && folder !== null) void handleFiles(files, folder);
    },
    [handleFiles],
  );

  const handleDelete = useCallback(async () => {
    if (!deleteEntry) return;
    const ok = await deleteFile(deleteEntry.path);
    if (ok) {
      toast.success(`Deleted ${deleteEntry.name}`);
      setDeleteEntry(null);
      if (previewEntry?.fileId === deleteEntry.fileId) openPreview(null);
    } else {
      toast.error(`Failed to delete ${deleteEntry.name}`);
    }
  }, [deleteEntry, deleteFile, toast, previewEntry, openPreview]);

  const handleDeleteFolder = useCallback(async () => {
    if (!deleteFolderPath) return;
    const folderName = deleteFolderPath.split('/').pop() || deleteFolderPath;
    const count = await deleteFolder(deleteFolderPath);
    if (count !== null) {
      const suffix = count === 1 ? '1 file' : `${count} files`;
      toast.success(`Deleted folder ${folderName} (${suffix})`);
      // Close the preview if the previewed file lived under the deleted
      // prefix — the per-file `vfs.file.deleted` events will also handle
      // this, but doing it eagerly avoids a flash of a stale preview.
      if (
        previewEntry &&
        (previewEntry.path === deleteFolderPath ||
          previewEntry.path.startsWith(`${deleteFolderPath}/`))
      ) {
        openPreview(null);
      }
      setDeleteFolderPath(null);
    } else {
      toast.error(`Failed to delete folder ${folderName}`);
    }
  }, [deleteFolderPath, deleteFolder, toast, previewEntry, openPreview]);

  const handleDownload = useCallback(
    async (entry: VfsEntry) => {
      try {
        const url = await backend.getDownloadUrl(entry.path);
        await downloadFileFromUrl(url, entry.name);
      } catch {
        toast.error(`Failed to download ${entry.name}`);
      }
    },
    [backend, toast],
  );

  // Copy a shareable deep link to the file (host page supplies the app-relative
  // path via `buildShareUrl`; we prepend the current origin for an absolute,
  // paste-anywhere URL). Only wired when `buildShareUrl` is provided.
  const handleCopyLink = useMemo(
    () =>
      buildShareUrl
        ? async (entry: VfsEntry) => {
            try {
              const url = window.location.origin + buildShareUrl(entry);
              await navigator.clipboard.writeText(url);
              toast.success('Link copied');
            } catch {
              toast.error('Could not copy link');
            }
          }
        : undefined,
    [buildShareUrl, toast],
  );

  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  const openNewFolderDialog = useCallback(() => {
    setNewFolderName('');
    setNewFolderOpen(true);
  }, []);

  const submitNewFolder = useCallback(async () => {
    const trimmed = newFolderName.trim().replace(/^\/+|\/+$/g, '');
    if (!trimmed) return;
    const fullPath = rootPath ? `${rootPath}/${trimmed}` : trimmed;
    try {
      await mkdir({ path: fullPath });
      // Lazy backends: show the new folder immediately in the store (the
      // `kb.vfs.folder.created` event confirms it shortly after).
      if (isLazy) lazy.optimisticFolder(fullPath);
      // Auto-expand the newly-created folder so the user can immediately
      // drop files into it.
      setFolders((prev) => ({ ...prev, [fullPath]: true }));
      setNewFolderOpen(false);
      setNewFolderName('');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not create folder';
      toast.error(msg);
    }
  }, [mkdir, newFolderName, rootPath, toast, isLazy, lazy]);

  const startRename = useCallback((entry: VfsEntry) => {
    setRenamingFileId(entry.fileId);
  }, []);

  const finishRename = useCallback(
    async (entry: VfsEntry, nextName: string) => {
      const trimmed = nextName.trim();
      // Validation: non-empty, no slashes (filename-only rename, not move),
      // and a real change. Invalid input keeps the editor open by throwing
      // so InlineEdit sees a rejected save and leaves itself mounted.
      if (!trimmed || trimmed.includes('/') || trimmed === entry.name) {
        setRenamingFileId(null);
        return;
      }
      const lastSlash = entry.path.lastIndexOf('/');
      const dir = lastSlash >= 0 ? entry.path.slice(0, lastSlash) : '';
      const toPath = dir ? `${dir}/${trimmed}` : trimmed;
      setRenamingFileId(null);
      await doMove(entry.path, toPath);
    },
    [doMove],
  );

  const cancelRename = useCallback(() => {
    setRenamingFileId(null);
  }, []);

  // Folder rename — same UX as file rename: 3-dot menu → "Rename"
  // toggles an InlineEdit on the folder name. The actual rename is just
  // a move with a new basename (the KB backend handles both files and
  // folders through the same /nodes/move endpoint).
  const [renamingFolderPath, setRenamingFolderPath] = useState<string | null>(null);

  const startFolderRename = useCallback((path: string) => {
    setRenamingFolderPath(path);
  }, []);

  const cancelFolderRename = useCallback(() => {
    setRenamingFolderPath(null);
  }, []);

  const finishFolderRename = useCallback(
    async (path: string, nextName: string) => {
      const trimmed = nextName.trim();
      const lastSlash = path.lastIndexOf('/');
      const baseName = lastSlash >= 0 ? path.slice(lastSlash + 1) : path;
      if (!trimmed || trimmed.includes('/') || trimmed === baseName) {
        setRenamingFolderPath(null);
        return;
      }
      const dir = lastSlash >= 0 ? path.slice(0, lastSlash) : '';
      const toPath = dir ? `${dir}/${trimmed}` : trimmed;
      setRenamingFolderPath(null);
      await doMove(path, toPath);
    },
    [doMove],
  );

  if (!ownerId) {
    return (
      <div className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
        Files: no owner context available.
      </div>
    );
  }

  const pendingJobs = jobs.filter((j) => j.status !== 'done');

  // Root drop highlight: panel is being dragged on AND no folder row has
  // claimed the target AND we're not dragging FROM the root level itself
  // (otherwise dropping at the root would be a no-op).
  const rootIsSource = draggingSourceDir === (rootPath || '');
  const rootDropActive = isDragging && !activeDropPath && !rootIsSource;

  const showPreviewPane = !!previewEntry;
  const treePaneStyle: React.CSSProperties | undefined =
    showPreviewPane && !isStacked ? { width: treeWidth, flex: '0 0 auto' } : undefined;
  // Hide the size + date columns whenever the preview pane is open in
  // a side-by-side layout — the user's focus has moved to the preview
  // and the metadata columns become visual filler. Stacked layout
  // keeps them since the tree has the full width to play with.
  const isCompactRow = showPreviewPane && !isStacked;

  // List view is a flat recursive listing — only available on lazy backends
  // once the server's `recursive=true` returns every nested file (gated by
  // the `recursiveList` capability). Until then KB is tree-only.
  const showListToggle = !isLazy || backend.capabilities.recursiveList;
  const effectiveView: ViewMode = showListToggle ? view : 'tree';

  // Header count: eager backends know the exact total; lazy backends only know
  // it if the backend reports a per-root total (otherwise the count is hidden
  // rather than shown misleadingly small).
  const headerCount = isLazy
    ? lazy.store.get(rootPath)?.totalCount ?? null
    : visible.length;

  const showInitialSpinner = isLazy
    ? lazy.rootStatus === 'loading' && flatRows.length === 0
    : isLoading && entries.length === 0;
  const showEmptyState = isLazy
    ? lazy.rootStatus === 'loaded' && flatRows.length === 0
    : visible.length === 0 && extraFolderPaths.length === 0;

  const searchPanel = searchOp && searchOpen ? (
    <SearchPanel
      searchOp={searchOp}
      selectedPath={previewEntry?.path ?? null}
      onOpenResult={revealAndOpen}
    />
  ) : null;

  const treeBody = (
    <div className="relative flex flex-col min-h-0 flex-1">
      {searchPanel}
      {searchOpen ? null : error ? (
        <div className="px-4 py-8 text-sm text-destructive">Failed to load files: {error.message}</div>
      ) : showInitialSpinner ? (
        <div className="flex min-h-0 flex-1 items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : showEmptyState ? (
        <EmptyState allowUpload={allowUpload} />
      ) : effectiveView === 'tree' ? (
        <FilesTree
          rows={flatRows}
          onToggleFolder={toggleFolder}
          onLoadMore={lazy.loadMore}
          onRetry={lazy.reload}
          onOpen={openPreview}
          onDownload={handleDownload}
          onCopyLink={handleCopyLink}
          onDelete={allowDelete ? (e) => setDeleteEntry(e) : undefined}
          onDeleteFolder={allowDelete ? (path) => setDeleteFolderPath(path) : undefined}
          onUploadToFolder={allowUpload ? triggerFolderUpload : undefined}
          onRenameStart={allowUpload ? startRename : undefined}
          onRenameSave={allowUpload ? finishRename : undefined}
          onRenameCancel={cancelRename}
          onFolderRenameStart={allowUpload ? startFolderRename : undefined}
          onFolderRenameSave={allowUpload ? finishFolderRename : undefined}
          onFolderRenameCancel={cancelFolderRename}
          renamingFileId={renamingFileId}
          renamingFolderPath={renamingFolderPath}
          ownerId={ownerId}
          compact={isCompactRow}
          dateColumn={dateColumn}
          highlightedFileIds={highlighted}
          highlightedFolderPaths={highlightedFolderPaths}
        />
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <ListView
            entries={visible}
            onOpen={openPreview}
            onDownload={handleDownload}
            onCopyLink={handleCopyLink}
            onDelete={allowDelete ? (e) => setDeleteEntry(e) : undefined}
            onRenameStart={allowUpload ? startRename : undefined}
            onRenameSave={allowUpload ? finishRename : undefined}
            onRenameCancel={cancelRename}
            renamingFileId={renamingFileId}
            ownerId={ownerId}
            compact={isCompactRow}
            dateColumn={dateColumn}
            highlightedFileIds={highlighted}
          />
        </div>
      )}

      {pendingJobs.length > 0 && (
        <div className="border-t border-border bg-muted/30 px-3 py-2 space-y-1">
          {pendingJobs.map((j) => (
            <div key={j.path} className="flex items-center gap-2 text-xs">
              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
              <span className="font-mono text-muted-foreground truncate flex-1">{j.path}</span>
              <span className="text-muted-foreground tabular-nums">
                {j.status === 'error' ? 'failed' : `${Math.round(j.progress * 100)}%`}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <FilesDropContext.Provider value={dropCtxValue}>
    <div
      ref={containerRef}
      className={cn(
        // `h-full min-h-0` lets the panel fill its parent's allotted
        // height instead of growing with content — so opening the
        // preview pane, swapping files, or loading large content no
        // longer shifts the surrounding layout. Hosts that don't
        // provide a definite height should wrap AuiFiles in a sized
        // container (e.g. `h-[600px]` on the KB pages); AUI canvas
        // surfaces already provide one via the grid track.
        'flex h-full min-h-0 flex-col rounded-lg border border-border bg-card overflow-hidden transition-shadow',
        isDragging && 'ring-1 ring-primary/30',
      )}
      onDragEnter={allowUpload ? handlePanelDragEnter : undefined}
      onDragOver={allowUpload ? handlePanelDragOver : undefined}
      onDragLeave={allowUpload ? handlePanelDragLeave : undefined}
      onDrop={allowUpload ? handlePanelDrop : undefined}
      data-testid="aui-files-panel"
    >
      {/* Header — also serves as the root drop target. Halo is a
       *  floating absolutely-positioned child that sits OUTSIDE the
       *  header's bounds so the header doesn't reflow when it appears. */}
      <div
        className={cn(
          'relative flex items-center gap-3 border-b border-border px-3 py-2 transition-colors',
          rootDropActive && 'bg-primary/10',
        )}
      >
        {rootDropActive && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-[-2px] border-2 border-dotted border-primary"
          />
        )}
        <div className="flex items-center gap-2 min-w-0">
          <FolderOpen className={cn('h-4 w-4 shrink-0', rootDropActive ? 'text-primary' : 'text-muted-foreground')} />
          <span className={cn('text-sm font-medium truncate', rootDropActive ? 'text-primary' : 'text-foreground')}>
            {title}
          </span>
          {rootPath && (
            <span className="text-xs text-muted-foreground truncate font-mono">/{rootPath}</span>
          )}
          {rootDropActive ? (
            <span className="text-xs font-medium text-primary shrink-0">— drop here for root</span>
          ) : headerCount !== null ? (
            <span className="text-xs text-muted-foreground shrink-0">({headerCount})</span>
          ) : null}
        </div>
        {searchOp && (
          <HeaderSearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            onSubmit={async (q) => {
              if (!q.trim()) return;
              setSearchOpen(true);
              try {
                await searchOp.mutateAsync({
                  query: q.trim(),
                  topK: 20,
                  includeText: true,
                });
              } catch {
                /* error surfaces via searchOp.error in the panel */
              }
            }}
            onClear={() => {
              setSearchQuery('');
              setSearchOpen(false);
            }}
            isPending={searchOp.isPending}
            className="ml-auto"
          />
        )}
        <div className="flex items-center gap-1">
          {showListToggle && (
            <button
              type="button"
              onClick={() => setView(effectiveView === 'tree' ? 'list' : 'tree')}
              className="inline-flex items-center justify-center rounded-md h-7 w-7 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label={effectiveView === 'tree' ? 'Switch to list view' : 'Switch to tree view'}
              title={effectiveView === 'tree' ? 'Switch to list view' : 'Switch to tree view'}
            >
              {effectiveView === 'tree' ? <ListIcon className="h-4 w-4" /> : <FolderTree className="h-4 w-4" />}
            </button>
          )}
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="inline-flex items-center justify-center rounded-md h-7 w-7 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Refresh file list"
            title="Refresh file list"
          >
            <RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />
          </button>
          {backend.capabilities.folders && (
            <button
              type="button"
              onClick={openNewFolderDialog}
              disabled={isCreatingFolder}
              className="inline-flex items-center justify-center rounded-md h-7 w-7 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="New folder"
              title="New folder"
            >
              <FolderPlus className="h-4 w-4" />
            </button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-md h-7 w-7 text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label="Date column"
                title="Date column"
              >
                <CalendarIcon className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setDateColumn('created')}>
                <Check
                  className={cn(
                    'h-3.5 w-3.5',
                    dateColumn === 'created' ? 'opacity-100' : 'opacity-0',
                  )}
                />
                Date created
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setDateColumn('modified')}>
                <Check
                  className={cn(
                    'h-3.5 w-3.5',
                    dateColumn === 'modified' ? 'opacity-100' : 'opacity-0',
                  )}
                />
                Date modified
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {allowUpload && (
            <UploadButton onFiles={(files) => handleFiles(files)} />
          )}
        </div>
      </div>

      {/* Split body — tree always on the left/top, preview pane appears
       *  when a file is selected. Stacked layout below STACK_BELOW_PX. */}
      <div
        className={cn(
          'flex min-h-[12rem] flex-1',
          isStacked ? 'flex-col' : 'flex-row',
        )}
      >
        <div
          className={cn(
            'flex flex-col min-h-0 min-w-0',
            // Side-by-side: tree pane grows to fill (or inline-style override
            // pins it to treeWidth when a preview is open). Stacked layout
            // keeps default flex sizing so the preview can claim the
            // remaining vertical space.
            !isStacked && 'flex-1',
          )}
          style={treePaneStyle}
        >
          {treeBody}
        </div>

        {showPreviewPane && !isStacked && (
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize tree pane"
            onMouseDown={handleResizeMouseDown}
            className="w-1 shrink-0 cursor-col-resize bg-border/60 hover:bg-border transition-colors"
          />
        )}

        {showPreviewPane && (
          <div
            className={cn(
              'flex min-h-0 flex-1 min-w-0',
              isStacked && 'border-t border-border',
            )}
          >
            <AuiFilePreview
              backend={backend}
              entry={previewEntry!}
              onClose={() => openPreview(null)}
              allowDelete={allowDelete}
              onDelete={async () => { setDeleteEntry(previewEntry); }}
              className="flex-1"
            />
          </div>
        )}
      </div>

      {/* Hidden picker shared by all folder-row "Upload to this folder"
       *  menu items. The active folder is tracked via a ref so we can
       *  reuse a single input element. */}
      <input
        ref={folderUploadInputRef}
        type="file"
        multiple
        hidden
        onChange={handleFolderUploadInputChange}
      />

      {deleteEntry && (
        <ConfirmModal
          isOpen={true}
          onClose={() => setDeleteEntry(null)}
          onConfirm={() => void handleDelete()}
          title="Delete file"
          message={`Delete ${deleteEntry.name}? This cannot be undone.`}
          confirmText="Delete"
          isLoading={isDeleting}
        />
      )}

      {deleteFolderPath && (
        <ConfirmModal
          isOpen={true}
          onClose={() => setDeleteFolderPath(null)}
          onConfirm={() => void handleDeleteFolder()}
          title="Delete folder"
          message={`Delete folder "${deleteFolderPath.split('/').pop() || deleteFolderPath}" and all its contents? This cannot be undone.`}
          confirmText="Delete folder"
          isLoading={isDeleting}
        />
      )}

      <Modal
        isOpen={newFolderOpen}
        onClose={() => {
          if (!isCreatingFolder) setNewFolderOpen(false);
        }}
        title="New folder"
        size="sm"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setNewFolderOpen(false)}
              disabled={isCreatingFolder}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void submitNewFolder()}
              isLoading={isCreatingFolder}
              disabled={!newFolderName.trim()}
            >
              Create
            </Button>
          </>
        }
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submitNewFolder();
          }}
          className="space-y-2"
        >
          <Label htmlFor="new-folder-name">Folder name</Label>
          <Input
            id="new-folder-name"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            placeholder='e.g. "docs" or "drafts/q1"'
            autoFocus
            disabled={isCreatingFolder}
          />
          <p className="text-xs text-muted-foreground">
            Use <span className="font-mono">/</span> to create nested folders.
          </p>
        </form>
      </Modal>

      {pendingCopy && (
        <Modal
          isOpen={true}
          onClose={() => setPendingCopy(null)}
          title="File already exists"
          size="sm"
          footer={
            <>
              <Button variant="outline" onClick={() => setPendingCopy(null)}>
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  const req = pendingCopy;
                  const filename = req.destPath.split('/').pop() ?? req.destPath;
                  const reserved = new Set<string>(
                    entriesRef.current.map((e) => e.path),
                  );
                  const { path } = resolveCollisionFreePath(
                    filename,
                    req.destDir,
                    reserved,
                  );
                  setPendingCopy(null);
                  void runCrossCopy(req, path);
                }}
              >
                Keep both
              </Button>
              <Button
                onClick={() => {
                  const req = pendingCopy;
                  setPendingCopy(null);
                  void runCrossCopy(req, req.destPath);
                }}
              >
                Replace
              </Button>
            </>
          }
        >
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">
              {pendingCopy.destPath.split('/').pop()}
            </span>{' '}
            already exists in this folder. Replace it, or keep both?
          </p>
        </Modal>
      )}
    </div>
    </FilesDropContext.Provider>
  );
}

// ─── Tree view (virtualized) ──────────────────────────────────────────
//
// One flat, windowed list of rows (`FlatRow[]`, built by `flattenTree`). The
// tree is materialised upstream in AuiFilesInner — this component only renders
// the visible slice and owns keyboard navigation. Sentinel rows (spinner /
// empty / error / load-more) come from the lazy store's per-folder state.

/** Stable virtualizer key per row — sentinels share a folder path, so they
 *  need the variant to disambiguate. */
function rowKey(row: FlatRow): string {
  return row.variant === 'folder' || row.variant === 'file'
    ? row.path
    : `${row.path}::${row.variant}`;
}

/** Indent (px) for a row at `depth`. Files + sentinels sit one notch right of
 *  sibling folders so they align past the folder's chevron. */
function rowIndent(depth: number, kind: 'folder' | 'leaf'): number {
  const base = depth * 12 + 8;
  return kind === 'folder' ? base : base + 18;
}

interface RowCallbacks {
  onToggleFolder: (path: string) => void;
  onLoadMore: (path: string) => void;
  onRetry: (path: string) => void;
  onOpen: (e: VfsEntry) => void;
  onDownload: (e: VfsEntry) => void;
  onCopyLink?: (e: VfsEntry) => void;
  onDelete?: (e: VfsEntry) => void;
  onDeleteFolder?: (path: string) => void;
  onUploadToFolder?: (path: string) => void;
  onRenameStart?: (e: VfsEntry) => void;
  onRenameSave?: (e: VfsEntry, next: string) => Promise<void> | void;
  onRenameCancel?: () => void;
  onFolderRenameStart?: (path: string) => void;
  onFolderRenameSave?: (path: string, next: string) => Promise<void> | void;
  onFolderRenameCancel?: () => void;
  renamingFileId?: string | null;
  renamingFolderPath?: string | null;
  ownerId: string;
  compact?: boolean;
  dateColumn?: DateColumn;
  highlightedFileIds?: Map<string, { since: number; seq: number }>;
  highlightedFolderPaths?: Set<string>;
}

function FilesTree({
  rows,
  ...cb
}: { rows: FlatRow[] } & RowCallbacks) {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Navigable (folder/file) paths only — sentinels are skipped by arrow nav.
  const navPaths = useMemo(
    () => rows.filter(isNavigableRow).map((r) => r.path),
    [rows],
  );

  const [focusedPath, setFocusedPath] = useState<string | null>(null);
  const tabbablePath = focusedPath ?? navPaths[0] ?? null;

  // Heal stale focus when the visible set changes (folder collapsed, file
  // deleted, root reloaded): drop a focus that's no longer present.
  useEffect(() => {
    if (focusedPath && !navPaths.includes(focusedPath)) setFocusedPath(null);
  }, [navPaths, focusedPath]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => (cb.compact ? 28 : 30),
    overscan: 12,
    getItemKey: (i) => rowKey(rows[i]),
  });

  // Only window large lists. Below the threshold we render every row directly
  // — simpler, and robust to zero-height containers (the virtualizer renders
  // nothing when the scroll viewport has no measured height, e.g. in jsdom).
  const shouldVirtualize = rows.length > VIRTUALIZE_THRESHOLD;

  // After arrow-nav advances `focusedPath`, scroll the row into view (the
  // virtualizer may have it unmounted) then move DOM focus to it — only while
  // the user is already navigating inside the tree.
  useEffect(() => {
    if (!focusedPath) return;
    const container = scrollRef.current;
    if (!container || !container.contains(document.activeElement)) return;
    if (shouldVirtualize) {
      const idx = rows.findIndex((r) => r.path === focusedPath && isNavigableRow(r));
      if (idx >= 0) virtualizer.scrollToIndex(idx, { align: 'auto' });
    }
    requestAnimationFrame(() => {
      const el = container.querySelector<HTMLElement>(
        `[data-tree-path="${CSS.escape(focusedPath)}"]`,
      );
      el?.focus();
    });
  }, [focusedPath, rows, virtualizer, shouldVirtualize]);

  const focusByOffset = useCallback(
    (from: string, delta: number) => {
      const i = navPaths.indexOf(from);
      if (i < 0) return;
      const next = navPaths[i + delta];
      if (next) setFocusedPath(next);
    },
    [navPaths],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!tabbablePath) return;
      const row = rows.find((r) => r.path === tabbablePath && isNavigableRow(r));
      if (!row) return;
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          focusByOffset(tabbablePath, 1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          focusByOffset(tabbablePath, -1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (row.variant === 'folder') {
            if (!row.open) cb.onToggleFolder(row.path);
            else focusByOffset(tabbablePath, 1);
          } else if (row.entry) {
            cb.onOpen(row.entry);
          }
          break;
        case 'ArrowLeft': {
          e.preventDefault();
          if (row.variant === 'folder' && row.open) {
            cb.onToggleFolder(row.path);
          } else {
            const parent = parentOfPath(row.path);
            if (parent && navPaths.includes(parent)) setFocusedPath(parent);
          }
          break;
        }
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (row.variant === 'folder') cb.onToggleFolder(row.path);
          else if (row.entry) cb.onOpen(row.entry);
          break;
        case 'Home':
          e.preventDefault();
          if (navPaths[0]) setFocusedPath(navPaths[0]);
          break;
        case 'End': {
          e.preventDefault();
          const last = navPaths[navPaths.length - 1];
          if (last) setFocusedPath(last);
          break;
        }
      }
    },
    [tabbablePath, rows, navPaths, focusByOffset, cb],
  );

  const renderRow = (row: FlatRow) => (
    <TreeRowView
      row={row}
      tabbable={tabbablePath === row.path}
      onFocusItem={setFocusedPath}
      {...cb}
    />
  );

  return (
    <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto">
      {shouldVirtualize ? (
        <div
          role="tree"
          onKeyDown={handleKeyDown}
          className="relative w-full py-1"
          style={{ height: virtualizer.getTotalSize() }}
        >
          {virtualizer.getVirtualItems().map((vi) => (
            <div
              key={vi.key}
              data-index={vi.index}
              ref={virtualizer.measureElement}
              className="absolute left-0 top-0 w-full"
              style={{ transform: `translateY(${vi.start}px)` }}
            >
              {renderRow(rows[vi.index])}
            </div>
          ))}
        </div>
      ) : (
        <div role="tree" onKeyDown={handleKeyDown} className="w-full py-1">
          {rows.map((row) => (
            <div key={rowKey(row)}>{renderRow(row)}</div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Single-row dispatcher — renders a folder, file, or sentinel by variant.
 *  Never recurses (the flat list already encodes the whole visible tree). */
function TreeRowView({
  row,
  tabbable,
  onFocusItem,
  ...cb
}: {
  row: FlatRow;
  tabbable: boolean;
  onFocusItem: (path: string) => void;
} & RowCallbacks) {
  switch (row.variant) {
    case 'folder':
      return (
        <FolderRow
          row={row}
          tabbable={tabbable}
          onFocusItem={onFocusItem}
          {...cb}
        />
      );
    case 'file':
      if (!row.entry) return null;
      return (
        <FileRow
          entry={row.entry}
          indent={rowIndent(row.depth, 'leaf')}
          onOpen={cb.onOpen}
          onDownload={cb.onDownload}
          onCopyLink={cb.onCopyLink}
          onDelete={cb.onDelete}
          onRenameStart={cb.onRenameStart}
          onRenameSave={cb.onRenameSave}
          onRenameCancel={cb.onRenameCancel}
          isRenaming={cb.renamingFileId === row.entry.fileId}
          ownerId={cb.ownerId}
          compact={cb.compact}
          dateColumn={cb.dateColumn}
          tree={{ tabbable, depth: row.depth, onFocus: () => onFocusItem(row.path) }}
          highlight={cb.highlightedFileIds?.get(row.entry.fileId)}
        />
      );
    case 'spinner':
      return (
        <div
          className="flex items-center gap-2 py-1 pr-2 text-xs text-muted-foreground"
          style={{ paddingLeft: rowIndent(row.depth, 'leaf') }}
        >
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          <span>Loading…</span>
        </div>
      );
    case 'empty':
      return (
        <div
          className="py-1 pr-2 text-xs italic text-muted-foreground"
          style={{ paddingLeft: rowIndent(row.depth, 'leaf') }}
        >
          Empty folder
        </div>
      );
    case 'error':
      return (
        <div
          className="flex items-center gap-2 py-1 pr-2 text-xs text-destructive"
          style={{ paddingLeft: rowIndent(row.depth, 'leaf') }}
        >
          <span>{row.message ?? 'Failed to load'}</span>
          <button
            type="button"
            onClick={() => cb.onRetry(row.path)}
            className="underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      );
    case 'load-more':
      return (
        <div className="py-1 pr-2" style={{ paddingLeft: rowIndent(row.depth, 'leaf') }}>
          <button
            type="button"
            onClick={() => cb.onLoadMore(row.path)}
            disabled={row.loadingMore}
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            {row.loadingMore ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
            {row.loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      );
    default:
      return null;
  }
}

/** One folder chrome row. Folder-aware drag-and-drop, rename-in-place, and the
 *  actions menu live here; children are sibling rows in the flat list, not
 *  nested DOM (the virtualizer needs a flat structure). */
function FolderRow({
  row,
  tabbable,
  onFocusItem,
  onToggleFolder,
  onUploadToFolder,
  onFolderRenameStart,
  onFolderRenameSave,
  onFolderRenameCancel,
  onDeleteFolder,
  renamingFolderPath,
  compact,
  highlightedFileIds,
  highlightedFolderPaths,
}: { row: FlatRow; tabbable: boolean; onFocusItem: (path: string) => void } & RowCallbacks) {
  const node = row.node!;
  const depth = row.depth;
  const open = !!row.open;
  const canExpand = !!row.canExpand;
  const indent = rowIndent(depth, 'folder');
  const dropCtx = useContext(FilesDropContext);
  const isDropTarget = dropCtx?.activeDropPath === node.path;

  const folderDragHandlers = dropCtx
    ? {
        onDragEnter: (e: React.DragEvent) => {
          e.preventDefault();
          e.stopPropagation();
          if (dropCtx.draggingSourceDir === node.path) return;
          if (dropCtx.activeDropPath !== node.path) dropCtx.setActiveDropPath(node.path);
        },
        onDragOver: (e: React.DragEvent) => {
          e.preventDefault();
          e.stopPropagation();
          if (dropCtx.draggingSourceDir === node.path) return;
          if (dropCtx.activeDropPath !== node.path) dropCtx.setActiveDropPath(node.path);
        },
        onDragLeave: (e: React.DragEvent) => {
          e.preventDefault();
          e.stopPropagation();
          const related = e.relatedTarget as Node | null;
          const rowEl = e.currentTarget as Node;
          if (related && rowEl.contains(related)) return;
          if (dropCtx.activeDropPath === node.path) dropCtx.setActiveDropPath(null);
        },
        onDrop: (e: React.DragEvent) => {
          e.preventDefault();
          e.stopPropagation();
          void dropCtx.processDrop(e, node.path);
        },
      }
    : {};

  return (
    <div {...folderDragHandlers}>
      <div
        role="treeitem"
        aria-expanded={open}
        aria-level={depth + 1}
        tabIndex={tabbable ? 0 : -1}
        data-tree-path={node.path}
        onFocus={() => onFocusItem(node.path)}
        onClick={(e) => {
          e.stopPropagation();
          e.currentTarget.focus();
          if (canExpand) onToggleFolder(node.path);
        }}
        className={cn(
          'relative group/folder flex items-center gap-1.5 py-1 pr-2 text-sm text-foreground transition-colors cursor-pointer focus:outline-none focus:bg-accent',
          isDropTarget ? 'bg-primary/10' : 'hover:bg-muted/60',
        )}
        style={{ paddingLeft: indent }}
        aria-dropeffect={isDropTarget ? 'copy' : undefined}
      >
        {isDropTarget && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-[-2px] rounded-sm border-2 border-dotted border-primary"
          />
        )}
        <div className="flex flex-1 items-center gap-1.5 min-w-0">
          {canExpand ? (
            <ChevronRight
              className={cn('h-3.5 w-3.5 text-muted-foreground transition-transform', open && 'rotate-90')}
            />
          ) : (
            <span aria-hidden className="inline-block h-3.5 w-3.5 shrink-0" />
          )}
          {open || isDropTarget ? (
            <FolderOpen className={cn('h-4 w-4', isDropTarget ? 'text-primary' : 'text-muted-foreground')} />
          ) : (
            <Folder className="h-4 w-4 text-muted-foreground" />
          )}
          {renamingFolderPath === node.path && onFolderRenameSave ? (
            <span
              className="flex-1 min-w-0"
              onKeyDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <InlineEdit
                value={node.name}
                externalEditing
                disableClickToEdit
                hideHoverHint
                onSave={async (next) => {
                  await onFolderRenameSave(node.path, next);
                }}
                onEditingChange={(editing) => {
                  if (!editing) onFolderRenameCancel?.();
                }}
                className="text-foreground font-medium"
                inputClassName="text-sm"
              />
            </span>
          ) : (
            (() => {
              const isFolderHighlighted =
                !!highlightedFolderPaths?.has(node.path) && !isDropTarget;
              const folderSeq = isFolderHighlighted ? highlightedFileIds?.size ?? 0 : 0;
              return (
                <span
                  key={isFolderHighlighted ? `hl-${folderSeq}` : 'name'}
                  className={cn(
                    'font-medium truncate',
                    isDropTarget && 'text-primary',
                    isFolderHighlighted && 'animate-file-highlight-fade',
                  )}
                >
                  {node.name}
                </span>
              );
            })()
          )}
        </div>
        {!compact && renamingFolderPath !== node.path && (
          <>
            <span aria-hidden className="w-20 shrink-0" />
            <span aria-hidden className="hidden w-44 shrink-0 md:inline-block" />
          </>
        )}
        {(onUploadToFolder || onFolderRenameStart || onDeleteFolder) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                tabIndex={-1}
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                aria-label="Folder actions"
                title="Folder actions"
                className="rounded-md h-6 w-6 inline-flex items-center justify-center text-muted-foreground opacity-0 group-hover/folder:opacity-100 hover:text-foreground hover:bg-muted data-[state=open]:opacity-100 data-[state=open]:bg-muted data-[state=open]:text-foreground transition-opacity"
              >
                <MoreVertical className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              {onUploadToFolder && (
                <DropdownMenuItem onSelect={() => onUploadToFolder(node.path)}>
                  <Upload className="h-4 w-4 text-muted-foreground" />
                  Upload to this folder
                </DropdownMenuItem>
              )}
              {onFolderRenameStart && (
                <DropdownMenuItem onSelect={() => onFolderRenameStart(node.path)}>
                  <Pencil className="h-4 w-4 text-muted-foreground" />
                  Rename
                </DropdownMenuItem>
              )}
              {onDeleteFolder && (
                <>
                  {(onUploadToFolder || onFolderRenameStart) && <DropdownMenuSeparator />}
                  <DropdownMenuItem
                    onSelect={() => onDeleteFolder(node.path)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete folder
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}

// ─── List view ────────────────────────────────────────────────────────

function ListView({
  entries,
  onOpen,
  onDownload,
  onCopyLink,
  onDelete,
  onRenameStart,
  onRenameSave,
  onRenameCancel,
  renamingFileId,
  ownerId,
  compact,
  dateColumn,
  highlightedFileIds,
}: {
  entries: VfsEntry[];
  onOpen: (e: VfsEntry) => void;
  onDownload: (e: VfsEntry) => void;
  onCopyLink?: (e: VfsEntry) => void;
  onDelete?: (e: VfsEntry) => void;
  onRenameStart?: (e: VfsEntry) => void;
  onRenameSave?: (e: VfsEntry, next: string) => Promise<void> | void;
  onRenameCancel?: () => void;
  renamingFileId?: string | null;
  ownerId: string;
  compact?: boolean;
  dateColumn?: DateColumn;
  highlightedFileIds?: Map<string, { since: number; seq: number }>;
}) {
  const sorted = useMemo(
    () => [...entries].sort((a, b) => a.path.localeCompare(b.path)),
    [entries],
  );
  return (
    <div className="py-1">
      {sorted.map((entry) => (
        <FileRow
          key={entry.fileId}
          entry={entry}
          indent={12}
          showFullPath
          onOpen={onOpen}
          onDownload={onDownload}
          onCopyLink={onCopyLink}
          onDelete={onDelete}
          onRenameStart={onRenameStart}
          onRenameSave={onRenameSave}
          onRenameCancel={onRenameCancel}
          isRenaming={renamingFileId === entry.fileId}
          ownerId={ownerId}
          compact={compact}
          dateColumn={dateColumn}
          highlight={highlightedFileIds?.get(entry.fileId)}
        />
      ))}
    </div>
  );
}

// ─── Shared file row ──────────────────────────────────────────────────

interface FileRowTreeProps {
  /** True when this row is the current roving-tabindex entry. */
  tabbable: boolean;
  /** Tree depth — zero-based; rendered as aria-level = depth + 1. */
  depth: number;
  /** Called on the row's `onFocus` so the TreeView can track focus. */
  onFocus: () => void;
}

function FileRow({
  entry,
  indent,
  showFullPath = false,
  onOpen,
  onDownload,
  onCopyLink,
  onDelete,
  onRenameStart,
  onRenameSave,
  onRenameCancel,
  isRenaming = false,
  ownerId,
  compact,
  dateColumn = 'modified',
  tree,
  highlight,
}: {
  entry: VfsEntry;
  indent: number;
  showFullPath?: boolean;
  onOpen: (e: VfsEntry) => void;
  onDownload: (e: VfsEntry) => void;
  onCopyLink?: (e: VfsEntry) => void;
  onDelete?: (e: VfsEntry) => void;
  onRenameStart?: (e: VfsEntry) => void;
  onRenameSave?: (e: VfsEntry, next: string) => Promise<void> | void;
  onRenameCancel?: () => void;
  isRenaming?: boolean;
  ownerId: string;
  compact?: boolean;
  dateColumn?: DateColumn;
  tree?: FileRowTreeProps;
  /** Set when this file is freshly created/updated/moved. The name span
   *  remounts on each `seq` bump so the 15s color-fade animation restarts
   *  cleanly on repeated triggers. */
  highlight?: { since: number; seq: number };
}) {
  const Icon = iconFor(entry);
  // While the inline-edit input is mounted, suppress row-level click /
  // focus-grab / drag so they don't fight with the editor.
  const draggable = !!tree && !isRenaming;
  const dropCtx = useContext(FilesDropContext);
  return (
    <div
      role={tree ? 'treeitem' : undefined}
      aria-level={tree ? tree.depth + 1 : undefined}
      tabIndex={tree ? (tree.tabbable ? 0 : -1) : undefined}
      data-tree-path={tree ? entry.path : undefined}
      onFocus={isRenaming ? undefined : tree?.onFocus}
      draggable={draggable}
      onDragStart={draggable
        ? (e) => {
            e.stopPropagation();
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData(
              VFS_DRAG_MIME,
              JSON.stringify({
                fileId: entry.fileId,
                ownerId,
                path: entry.path,
              }),
            );
            // Publish the source folder so folder rows can skip their
            // drop highlight when they ARE the source.
            const lastSlash = entry.path.lastIndexOf('/');
            const sourceDir = lastSlash >= 0 ? entry.path.slice(0, lastSlash) : '';
            dropCtx?.setDraggingSourceDir(sourceDir);
          }
        : undefined}
      onDragEnd={draggable
        ? () => { dropCtx?.setDraggingSourceDir(null); }
        : undefined}
      // Hovering a file row during a drag advertises the file's PARENT
      // folder as the drop target — Finder-style. Without this the
      // active-drop highlight only flips while the cursor is exactly
      // on the folder header, which is hard to aim at when a folder is
      // mostly children. Skip when this file's parent IS the source
      // dir (dropping there would be a no-op).
      onDragEnter={tree && dropCtx ? (e) => {
        if (!e.dataTransfer.types.includes(VFS_DRAG_MIME)) return;
        e.preventDefault();
        const lastSlash = entry.path.lastIndexOf('/');
        const parentDir = lastSlash >= 0 ? entry.path.slice(0, lastSlash) : '';
        if (dropCtx.draggingSourceDir === parentDir) return;
        dropCtx.setActiveDropPath(parentDir);
      } : undefined}
      onDragOver={tree && dropCtx ? (e) => {
        if (!e.dataTransfer.types.includes(VFS_DRAG_MIME)) return;
        e.preventDefault();
      } : undefined}
      onDrop={tree && dropCtx ? (e) => {
        // File rows accept moves only — the MIME guard skips any
        // external file/folder drop targeted at a file row. The move
        // itself dispatches through processDrop using the file's
        // parent dir as the target folder.
        if (!e.dataTransfer.types.includes(VFS_DRAG_MIME)) return;
        e.preventDefault();
        e.stopPropagation();
        const lastSlash = entry.path.lastIndexOf('/');
        const parentDir = lastSlash >= 0 ? entry.path.slice(0, lastSlash) : '';
        void dropCtx.processDrop(e, parentDir);
      } : undefined}
      className="group flex items-center gap-2 py-1 pr-2 text-sm hover:bg-muted/60 cursor-pointer focus:outline-none focus:bg-accent"
      style={{ paddingLeft: indent }}
      onClick={(e) => {
        if (isRenaming) return; // editor owns the row while active
        e.stopPropagation();
        if (tree) e.currentTarget.focus();
        onOpen(entry);
      }}
    >
      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
      {isRenaming && onRenameSave ? (
        <span
          className="flex-1 min-w-0"
          // The InlineEdit input handles its own keys (Enter/Esc); stop
          // them from bubbling to the tree's arrow-key handler.
          onKeyDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <InlineEdit
            value={entry.name}
            externalEditing
            disableClickToEdit
            hideHoverHint
            onSave={async (next) => { await onRenameSave(entry, next); }}
            onEditingChange={(editing) => { if (!editing) onRenameCancel?.(); }}
            className="text-foreground"
            inputClassName="text-sm"
          />
        </span>
      ) : (
        <span
          key={highlight ? `hl-${highlight.seq}` : 'name'}
          className={cn(
            'flex-1 min-w-0 truncate text-foreground',
            highlight && 'animate-file-highlight-fade',
          )}
        >
          {showFullPath ? entry.path : entry.name}
        </span>
      )}
      {!compact && !isRenaming && (
        <>
          <span className="w-20 shrink-0 text-right text-xs text-muted-foreground tabular-nums">
            {formatFileSize(entry.size)}
          </span>
          <span className="hidden w-44 shrink-0 text-right text-xs text-muted-foreground md:inline-block">
            {formatAppleDate(
              dateColumn === 'created' ? entry.createdAt : entry.updatedAt,
            )}
          </span>
        </>
      )}
      {!isRenaming && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              tabIndex={tree ? -1 : undefined}
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              aria-label="File actions"
              title="File actions"
              className="rounded-md h-6 w-6 inline-flex items-center justify-center text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-foreground hover:bg-muted data-[state=open]:opacity-100 data-[state=open]:bg-muted data-[state=open]:text-foreground transition-opacity"
            >
              <MoreVertical className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuItem onSelect={() => onOpen(entry)}>
              <Eye className="h-4 w-4 text-muted-foreground" />
              Open
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => void onDownload(entry)}>
              <Download className="h-4 w-4 text-muted-foreground" />
              Download
            </DropdownMenuItem>
            {onCopyLink && (
              <DropdownMenuItem onSelect={() => void onCopyLink(entry)}>
                <Link2 className="h-4 w-4 text-muted-foreground" />
                Copy link
              </DropdownMenuItem>
            )}
            {onRenameStart && (
              <DropdownMenuItem onSelect={() => onRenameStart(entry)}>
                <Pencil className="h-4 w-4 text-muted-foreground" />
                Rename
              </DropdownMenuItem>
            )}
            {onDelete && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => onDelete(entry)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────

// Use the shared util so chips + the Files tree show the same icons.
const iconFor = iconForFile;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Default panel title derived from the VFS owner ID prefix
 * (`con-` → Conversation, `kb-` → Knowledge Base, `skl-` → Skill).
 * Falls back to "Files" when the owner is unknown or the prefix
 * doesn't match a host the frontend recognizes. Override via the
 * surface's `title` prop.
 */
function ownerTypeLabel(ownerId: string | undefined): string {
  if (!ownerId) return 'Files';
  const prefix = ownerId.split('-')[0];
  switch (prefix) {
    case 'con': return 'Conversation Files';
    case 'kb': return 'Knowledge Base Files';
    case 'skl': return 'Skill Files';
    default: return 'Files';
  }
}

// Path of the parent folder, or null if at root level. Used by the
// keyboard ArrowLeft handler to climb out of a collapsed file/folder.
function parentOfPath(path: string): string | null {
  const idx = path.lastIndexOf('/');
  return idx > 0 ? path.slice(0, idx) : null;
}

function EmptyState({ allowUpload }: { allowUpload: boolean }) {
  return (
    // `flex-1 min-h-0` lets the empty state fill its parent's remaining
    // height; `justify-center` then vertically centres the content
    // instead of stacking it at the top under the header.
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-4 py-8 text-muted-foreground">
      <Folder className="h-8 w-8 mb-2" />
      <p className="text-sm">No files yet</p>
      {allowUpload && (
        <p className="text-xs mt-1">Drag files here or use the upload button</p>
      )}
    </div>
  );
}

/**
 * Persistent search input that lives next to the title in the AuiFiles
 * header. Submitting (Enter) fires `onSubmit`; the `X` button (only
 * visible when there's a query) clears the input and asks the host to
 * dismiss the results panel via `onClear`.
 */
function HeaderSearchInput({
  value,
  onChange,
  onSubmit,
  onClear,
  isPending,
  className,
}: {
  value: string;
  onChange: (q: string) => void;
  onSubmit: (q: string) => void | Promise<void>;
  onClear: () => void;
  isPending: boolean;
  className?: string;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void onSubmit(value);
      }}
      className={cn(
        'relative flex w-80 min-w-0 shrink items-center',
        className,
      )}
    >
      <SearchIcon className="pointer-events-none absolute left-2.5 h-3.5 w-3.5 text-foreground/70" />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClear();
        }}
        placeholder="Search files…"
        aria-label="Search files"
        // Suppress the browser's native cancel cross so we can render
        // our own consistent one — the native one only appears in some
        // engines and never gets themed.
        className="w-full rounded-md border border-muted-foreground/30 bg-muted/60 pl-8 pr-7 py-1 text-sm text-foreground placeholder:text-foreground/60 focus-ring [&::-webkit-search-cancel-button]:hidden"
      />
      {value && (
        <button
          type="button"
          onClick={onClear}
          aria-label="Clear search"
          title="Clear search"
          className="absolute right-1.5 inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="h-3 w-3" />
        </button>
      )}
      {isPending && !value && (
        <Loader2 className="absolute right-2.5 h-3.5 w-3.5 animate-spin text-muted-foreground" />
      )}
    </form>
  );
}

interface DocumentMatch {
  fileId: string;
  path: string;
  name: string;
  /** Highest-scoring chunk's score — what we surface as "the" match
   *  score until the UI can highlight individual chunks. */
  bestScore: number;
  /** How many chunks in this document hit the query. */
  chunkCount: number;
}

function SearchPanel({
  searchOp,
  selectedPath,
  onOpenResult,
}: {
  searchOp: UseSearchResult;
  /** Highlight the row matching this path (the file the user just
   *  opened from the results), so they can see which hit they're
   *  looking at. */
  selectedPath: string | null;
  onOpenResult: (path: string) => void;
}) {
  // Search returns one row per chunk, but clicking a result just
  // opens the whole document — we don't have a "jump to chunk" /
  // highlight affordance yet. Until we do, collapsing multiple
  // chunks of the same document into one row is the honest UI: pick
  // the best score, surface a small `N matches` chip so the user
  // knows the query hit more than once. Group by fileId because two
  // files can theoretically share a name in different folders.
  const documents: DocumentMatch[] = (() => {
    const byFileId = new Map<string, DocumentMatch>();
    for (const r of searchOp.data?.results ?? []) {
      const existing = byFileId.get(r.fileId);
      if (!existing) {
        byFileId.set(r.fileId, {
          fileId: r.fileId,
          path: r.path,
          name: r.name,
          bestScore: r.score ?? 0,
          chunkCount: 1,
        });
      } else {
        existing.bestScore = Math.max(existing.bestScore, r.score ?? 0);
        existing.chunkCount += 1;
      }
    }
    return [...byFileId.values()].sort((a, b) => b.bestScore - a.bestScore);
  })();

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-card">
      <div className="min-h-0 flex-1 overflow-y-auto">
        {!searchOp.isSuccess && !searchOp.isPending && !searchOp.error ? (
          <p className="px-3 py-2 text-xs text-muted-foreground">
            Type a query and press Enter.
          </p>
        ) : searchOp.isPending ? (
          <p className="px-3 py-2 text-xs text-muted-foreground">Searching…</p>
        ) : searchOp.error ? (
          <p className="px-3 py-2 text-xs text-destructive">
            {searchOp.error.message}
          </p>
        ) : documents.length === 0 ? (
          <p className="px-3 py-2 text-xs text-muted-foreground">No matches.</p>
        ) : (
          <ul>
            {documents.map((doc) => {
              const isSelected = selectedPath === doc.path;
              return (
                <li key={doc.fileId}>
                  <button
                    type="button"
                    onClick={() => onOpenResult(doc.path)}
                    className={cn(
                      'block w-full px-3 py-2 text-left transition-colors',
                      isSelected
                        ? 'bg-accent text-foreground'
                        : 'hover:bg-muted/60',
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-medium text-foreground">
                        {doc.name}
                      </span>
                      <span className="shrink-0 tabular-nums text-xs text-muted-foreground">
                        {(doc.bestScore * 100).toFixed(0)}%
                      </span>
                    </div>
                    <div className="mt-0.5 flex items-center justify-between gap-2">
                      <p className="truncate text-xs text-muted-foreground">
                        {doc.path}
                      </p>
                      {doc.chunkCount > 1 && (
                        <span
                          className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
                          title="Number of passages in this document that matched the query"
                        >
                          {doc.chunkCount} matches
                        </span>
                      )}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function UploadButton({ onFiles }: { onFiles: (files: File[]) => void }) {
  return (
    <DropZone onFiles={onFiles} multiple className="">
      {() => (
        <span
          className="inline-flex items-center justify-center rounded-md h-7 w-7 text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer"
          aria-label="Upload files"
          title="Upload files"
        >
          <Upload className="h-4 w-4" />
        </span>
      )}
    </DropZone>
  );
}
